import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, FormControl, Button, Table, Pagination, Modal, Spinner } from 'react-bootstrap';
import { graphql } from 'react-apollo';
import getStoreReviewsQuery from './getStoreReviews.graphql'; // Import the .graphql file
import generatePaginationItems from '../../../helpers/generatePaginationItems';
import './Skeleton.css';
import Link from '../../../components/Link/Link';

const StarRatingDisplay = ({ rating }) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
        if (i <= rating) {
            stars.push(<span key={i} style={{ color: 'gold' }}>★</span>); // Filled star
        } else {
            stars.push(<span key={i} style={{ color: 'lightgray' }}>★</span>); // Empty star
        }
    }
    return <div>{stars}</div>;
};

const StoreReviews = ({ title, data: { loading, error, getStoreReviews, refetch }, shopId  }) => {
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedReview, setSelectedReview] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [keyword, setKeyword] = useState('');
    const [ratingFilter, setRatingFilter] = useState('all');

    useEffect(() => {
        if (!loading) {
            refetch({
                currentPage,
                keyword,
                shopId,
                rating: ratingFilter === 'all' ? undefined : parseInt(ratingFilter)
            });
        }
    }, [currentPage, keyword, shopId, ratingFilter, refetch, loading]);

    const handleViewDetails = (review) => {
        setSelectedReview(review);
        setShowDetailModal(true);
    };

    const handleCloseDetailModal = () => {
        setShowDetailModal(false);
        setSelectedReview(null);
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const handleSearchChange = (e) => {
        setKeyword(e.target.value);
    };

    const handleRatingFilterChange = (e) => {
        setRatingFilter(e.target.value);
    };

        const Skeleton = () => <div className="skeleton-pulse"></div>;

    const ReviewSkeleton = () => (
        <tr>
            <td><Skeleton /></td>
            <td><Skeleton /></td>
            <td><Skeleton /></td>
            <td><Skeleton /></td>
            <td><Skeleton /></td>
            <td><Skeleton /></td>
        </tr>
    );

    if (loading) {
        return (
            <Container className='div-4'>
                <Row>
                    <Col>
                        <h1>{title}</h1>
                    </Col>
                </Row>
                <Row className="my-3">
                    <Col md={6}>
                        <Form className="d-flex">
                            <FormControl
                                type="search"
                                placeholder="Search by Order ID or Sender"
                                className="me-2"
                                aria-label="Search"
                            />
                            <Button variant="outline-success" type="submit">Search</Button>
                        </Form>
                    </Col>
                    <Col md={6} className="d-flex justify-content-end">
                        <Form.Group controlId="filterRating">
                            <Form.Label className="me-2">Filter by Rating:</Form.Label>
                            <Form.Control as="select" defaultValue="all">
                                <option value="all">All Ratings</option>
                                <option value="5">5 Stars</option>
                                <option value="4">4 Stars</option>
                                <option value="3">3 Stars</option>
                                <option value="2">2 Stars</option>
                                <option value="1">1 Star</option>
                            </Form.Control>
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Table striped bordered hover responsive>
                            <thead>
                                <tr>
                                    <th>Order ID</th>
                                    <th>Sender</th>
                                    <th>Rating</th>
                                    <th>Content</th>
                                    <th>Date</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <ReviewSkeleton />
                                <ReviewSkeleton />
                                <ReviewSkeleton />
                                <ReviewSkeleton />
                                <ReviewSkeleton />
                            </tbody>
                        </Table>
                    </Col>
                </Row>
            </Container>
        );
    }
    if (error) return <p>Error: {error.message}</p>;

    const reviews = getStoreReviews?.results || [];
    const totalCount = getStoreReviews?.count || 0;
    const totalPages = Math.ceil(totalCount / 10); // Assuming 10 items per page
    
    const paginationItems = generatePaginationItems(totalPages, currentPage, handlePageChange);
    return (
        <Container className='div-4'>
            <Row>
                <Col>
                    <h1>{title}</h1>
                </Col>
            </Row>
            <Row className="my-3">
                <Col md={6}>
                    <Form className="d-flex" onSubmit={(e) => { e.preventDefault(); refetch(); }}>
                        <FormControl
                            type="search"
                            placeholder="Search by Order ID or Sender"
                            className="me-2"
                            aria-label="Search"
                            value={keyword}
                            onChange={handleSearchChange}
                        />
                        <Button variant="outline-success" type="submit">Search</Button>
                    </Form>
                </Col>
                <Col md={6} className="d-flex justify-content-end">
                    <Form.Group controlId="filterRating">
                        <Form.Label className="me-2">Filter by Rating:</Form.Label>
                        <Form.Control as="select" defaultValue="all" onChange={handleRatingFilterChange}>
                            <option value="all">All Ratings</option>
                            <option value="5">5 Stars</option> 
                            <option value="4">4 Stars</option>
                            <option value="3">3 Stars</option>
                            <option value="2">2 Stars</option>
                            <option value="1">1 Star</option>
                        </Form.Control>
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col>
                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th>Order ID</th>
                                <th>Sender</th>
                                <th>Rating</th>
                                <th>Content</th>
                                <th>Date</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {reviews.length > 0 ? (
                                reviews.map((review) => (
                                    <tr key={review.id}>
                                        <td>#{review.bookingId}</td>
                                        <td>{review.sender}</td>
                                        <td>
                                            <StarRatingDisplay rating={review.ratings} />
                                        </td>
                                        <td>{review.reviewContent}</td>
                                        <td>{new Date(review.createdAt).toLocaleDateString()}</td>
                                        <td>
                                            <Button variant="info" size="sm" onClick={() => handleViewDetails(review)}>
                                                View
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5">No reviews found.</td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </Col>
            </Row>
            {totalPages > 1 && (
                <Row className="my-3">
                    <Col className="d-flex justify-content-center">
                        <Pagination>
                            <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                            <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                            {paginationItems}
                            <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                            <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
                        </Pagination>
                    </Col>
                </Row>
            )}

            {/* Review Detail Modal */}
            <Modal show={showDetailModal} onHide={handleCloseDetailModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Review Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedReview && (
                        <div>
                            <p><strong>Order ID:</strong> #{selectedReview.bookingId}</p>
                            <p><strong>Sender:</strong> {selectedReview.sender}</p>
                            <p><strong>Rating:</strong> <StarRatingDisplay rating={selectedReview.ratings} /></p>
                            {selectedReview.reviewContent && selectedReview.reviewContent !== 'null' && (
                                <p><strong>Content:</strong> {selectedReview.reviewContent}</p>
                            )}
                            <p><strong>Date:</strong> {new Date(selectedReview.createdAt).toLocaleDateString()}</p>
                            
                            <Link className='btn btn-primary' to={`/store/order-details/previous/${selectedReview.bookingId}`}>View Order</Link>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseDetailModal}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default graphql(getStoreReviewsQuery, {
    options: (props) => ({ 
        variables: {
            currentPage: 1,
            keyword: '',
            shopId: props.shopId,
            rating: undefined
        },
        fetchPolicy: 'network-only'
    })
})(StoreReviews);